import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { formatPrice } from "@/data/products";
import { toast } from "sonner";

const loadRzp = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function Checkout() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const express = params.get("express"); // 'upi' | 'gpay' | null
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState("razorpay");
  const [coupon, setCoupon] = useState("");
  const [guest, setGuest] = useState({ email: "", name: "" });
  const setG = (k) => (e) => setGuest((g) => ({ ...g, [k]: e.target.value }));
  const [addr, setAddr] = useState({ label:"Home", line1:"", line2:"", city:"", state:"", pincode:"", country:"India", phone:"" });
  const setF = (k) => (e) => setAddr({ ...addr, [k]: e.target.value });

  useEffect(() => { loadRzp(); }, []);

  // Live-payment test mode: cart made up only of the internal "Test · …" product
  // gets zero shipping and zero tax so you can pay exactly ₹1 via Razorpay.
  const isTestCart = items.length > 0 && items.every(i => (i.title || "").startsWith("Test · "));
  const shipping = 0;
  const tax = isTestCart ? 0 : Math.round(subtotal * 0.05);
  const discount = coupon.trim().toUpperCase() === "HOUSE10" ? Math.round(subtotal * 0.10) : 0;
  const total = subtotal + shipping + tax - discount;

  const runRazorpay = async (order) => {
    const ok = await loadRzp();
    if (!ok) { toast.error("Could not load Razorpay. Please try again."); return; }
    let cfg;
    try {
      // Check server configuration first so missing Hostinger environment variables
      // never turn into a vague "undefined" Razorpay error in the checkout.
      const config = await api.get("/payments/config");
      if (!config.data?.enabled || !config.data?.key_id) {
        toast.error("Razorpay is not configured on the server. Please contact the store administrator.");
        return;
      }
      const r = await api.post("/payments/create-order", { order_id: order.id });
      cfg = r.data;
      if (!cfg?.key_id || !cfg?.razorpay_order_id || !cfg?.amount) {
        toast.error("Razorpay configuration is incomplete on the server.");
        return;
      }
    } catch (e) {
      const msg = formatErr(e);
      toast.error(msg && msg !== "undefined" ? msg : "Razorpay could not be initialized. Please check the server configuration.");
      return;
    }
    return new Promise((resolve) => {
      const rzp = new window.Razorpay({
        key: cfg.key_id,
        amount: cfg.amount,
        currency: cfg.currency,
        name: "BARIQUE",
        description: `Order ${order.id}`,
        order_id: cfg.razorpay_order_id,
        prefill: { name: user?.name || guest.name || "", email: user?.email || guest.email || "", contact: addr.phone },
        // On mobile, when express UPI/GPay was selected, hint Razorpay to open
        // straight into the UPI intent picker (Razorpay Checkout supports this).
        method: express === "upi" || payment === "upi" ? { upi: true, netbanking: false, card: false, wallet: false } :
                payment === "card" ? { card: true, upi: false, netbanking: false, wallet: false } :
                express === "gpay" ? { upi: true, netbanking: false, card: false, wallet: false } : undefined,
        theme: { color: "#0A0A0A" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              order_id: order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment confirmed", { description: `Order ${order.id}` });
            clear();
            nav(user ? "/account?tab=orders" : `/order-confirmed?ref=${order.id}`);
            resolve(true);
          } catch (e) { toast.error(formatErr(e)); resolve(false); }
        },
        modal: { ondismiss: () => { toast("Payment cancelled — order held as pending"); resolve(false); } },
      });
      rzp.open();
    });
  };

  const place = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    if (!user && !guest.email.trim()) {
      toast.error("Please enter your email to continue as guest");
      return;
    }
    setBusy(true);
    let order;
    try {
      const r = await api.post("/orders", {
        items, address: addr, subtotal, shipping, tax, total, payment_method: payment, coupon,
        ...(user ? {} : { guest_email: guest.email, guest_name: guest.name }),
      });
      order = r.data;
    } catch (e) { toast.error(formatErr(e)); setBusy(false); return; }

    if (["razorpay", "card", "upi"].includes(payment)) {
      await runRazorpay(order);
    } else {
      toast.success("Order confirmed", { description: `Reference ${order.id}` });
      clear();
      nav(user ? "/account?tab=orders" : `/order-confirmed?ref=${order.id}`);
    }
    setBusy(false);
  };

  if (items.length === 0) return (
    <div className="pt-32 container-lux min-h-[60vh]">
      <h1 className="font-serif text-4xl">Your bag is empty</h1>
      <Link to="/collections" className="lux-btn-outline mt-8 inline-block">Discover Collections</Link>
    </div>
  );

  return (
    <div className="pt-28 pb-24 bg-ivory min-h-screen" data-testid="checkout-page">
      <div className="container-lux">
        <h1 className="font-serif tracking-tighter text-4xl md:text-6xl font-light">Checkout</h1>
        {express && (
          <p className="mt-4 overline text-muted-foreground" data-testid="express-hint">
            Express Checkout · {express === "upi" ? "UPI" : "Google Pay"} · pay in one tap after confirming your address
          </p>
        )}
        <form onSubmit={place} className="mt-14 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-14">
            {!user && (
              <div className="border border-border p-6 bg-white/40" data-testid="guest-notice">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                  <div>
                    <p className="overline">Guest Checkout</p>
                    <p className="mt-2 text-sm text-muted-foreground max-w-lg">
                      You're checking out as a guest. Your order confirmation and tracking will be emailed to you — no account required.
                    </p>
                  </div>
                  <Link to="/login?next=/checkout" data-testid="co-signin-instead" className="lux-btn-outline py-3 px-5 text-[10px] whitespace-nowrap">
                    Sign in instead
                  </Link>
                </div>
              </div>
            )}

            <Section title="01 · Contact">
              <div className="grid md:grid-cols-2 gap-6">
                {user ? (
                  <>
                    <Field label="Email" defaultValue={user.email} readOnly testid="co-email" required />
                    <Field label="Phone" value={addr.phone} onChange={setF("phone")} testid="co-phone" required />
                  </>
                ) : (
                  <>
                    <Field label="Full Name" value={guest.name} onChange={setG("name")} testid="co-guest-name" required />
                    <Field label="Email" type="email" value={guest.email} onChange={setG("email")} testid="co-email" required />
                    <Field label="Phone" value={addr.phone} onChange={setF("phone")} testid="co-phone" required className="md:col-span-2" />
                  </>
                )}
              </div>
            </Section>
            <Section title="02 · Shipping Address">
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Address Line 1" value={addr.line1} onChange={setF("line1")} testid="co-line1" required className="md:col-span-2" />
                <Field label="Address Line 2" value={addr.line2} onChange={setF("line2")} testid="co-line2" className="md:col-span-2" />
                <Field label="City" value={addr.city} onChange={setF("city")} testid="co-city" required />
                <Field label="State" value={addr.state} onChange={setF("state")} testid="co-state" required />
                <Field label="Pincode" value={addr.pincode} onChange={setF("pincode")} testid="co-pincode" required />
                <Field label="Country" value={addr.country} onChange={setF("country")} testid="co-country" required />
              </div>
            </Section>
            <Section title="03 · Payment">
              <div className="space-y-3">
                {[["razorpay","Razorpay · UPI, Cards, Netbanking"],["card","Credit / Debit Card"],["upi","UPI"],["cod","Cash on Delivery"]].map(([v,l]) => (
                  <label key={v} className={`flex items-center gap-4 border p-4 cursor-pointer ${payment===v?"border-ink":"border-border"}`}>
                    <input type="radio" name="pay" checked={payment===v} onChange={()=>setPayment(v)} data-testid={`pay-${v}`} />
                    <span className="text-sm">{l}</span>
                  </label>
                ))}
                <p className="text-xs text-muted-foreground mt-2">Card and UPI payments open securely through Razorpay Checkout. Available methods inside Razorpay depend on your Razorpay account and activated payment methods. Cash on Delivery remains an offline order option.</p>
              </div>
            </Section>
          </div>

          <aside className="bg-sand/60 p-10 h-max lg:sticky lg:top-28">
            <p className="overline">Order Summary</p>
            <div className="mt-8 space-y-6">
              {items.map((i, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="w-16 h-20 overflow-hidden bg-muted flex-shrink-0"><img src={i.image} alt="" className="w-full h-full object-cover"/></div>
                  <div className="flex-1 text-xs">
                    <p className="font-serif text-sm">{i.title}</p>
                    <p className="text-muted-foreground mt-1">{i.size} · Qty {i.quantity}</p>
                    <p className="mt-1">{formatPrice(i.price * i.quantity)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="divider-thin my-6" />
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="overline">Promo</label>
                <input value={coupon} onChange={(e)=>setCoupon(e.target.value)} placeholder="HOUSE10" className="lux-input" data-testid="co-coupon" />
              </div>
              <button type="button" onClick={()=>toast(coupon.toUpperCase()==="HOUSE10" ? "Promo applied" : "Invalid code")} className="lux-btn-outline py-3 px-4 text-[10px]" data-testid="co-apply-coupon">Apply</button>
            </div>
            <div className="mt-8 space-y-2 text-sm">
              <Row label="Subtotal" value={formatPrice(subtotal)} />
              <Row label="Shipping" value={shipping===0?"Complimentary":formatPrice(shipping)} />
              <Row label="Tax" value={formatPrice(tax)} />
              {discount > 0 && <Row label="Discount" value={"− " + formatPrice(discount)} />}
            </div>
            <div className="divider-thin my-6" />
            <div className="flex justify-between">
              <span className="overline">Total</span>
              <span className="font-serif text-2xl" data-testid="co-total">{formatPrice(total)}</span>
            </div>
            <button type="submit" disabled={busy} className="lux-btn-dark w-full mt-8" data-testid="place-order-btn">{busy ? "Processing…" : "Place Order"}</button>
          </aside>
        </form>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <h2 className="overline mb-6">{title}</h2>
    {children}
  </div>
);
const Field = ({ label, testid, className = "", ...r }) => (
  <div className={className}>
    <label className="overline text-muted-foreground">{label}</label>
    <input className="lux-input" data-testid={testid} {...r} />
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span>{value}</span></div>
);
